import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { GoogleMap, InfoWindowF, MarkerF, useJsApiLoader } from "@react-google-maps/api";
import { useIsDarkMode } from "../../theme/ThemeContext";
import { MAPS_LOADER_OPTIONS, MAP_STYLE_DARK, MAP_STYLE_LIGHT } from "../../utils/googleMaps";
import { Spinner } from "../UI/States";
import { formatAge, gpsPlace, gpsState, mapsUrl, vehicleModel } from "./fleetFormat";

// Live GPS Insight positions on a Google map. Used for the whole fleet on
// /fleet/vehicles and for one truck on its detail page.

const PIN_COLORS = {
  driving: "#0284c7",
  idling: "#f59e0b",
  parked: "#10b981",
  stale: "#94a3b8",
};

// Michiana — roughly the middle of the branch network.
const DEFAULT_CENTER = { lat: 41.7534, lng: -86.1108 };

export const MAP_LEGEND = [
  { key: "driving", label: "Driving" },
  { key: "idling", label: "Idling" },
  { key: "parked", label: "Parked" },
  { key: "stale", label: "No signal 24h+" },
];

const FleetMap = ({ vehicles, height = "min(65vh, 640px)", zoom = 8, showLinks = true }) => {
  const { isLoaded, loadError } = useJsApiLoader(MAPS_LOADER_OPTIONS);
  const isDark = useIsDarkMode();
  const mapRef = useRef(null);
  const [selectedKey, setSelectedKey] = useState(null);

  const pins = useMemo(
    () =>
      vehicles
        .filter((v) => v.gps && v.gps.lat !== null && v.gps.lng !== null)
        .map((v) => ({ vehicle: v, position: { lat: v.gps.lat, lng: v.gps.lng }, state: gpsState(v.gps) })),
    [vehicles]
  );

  // Re-frame only when the set of vehicles changes (a filter), not when the
  // same trucks report new positions on refresh.
  const pinSignature = pins.map((p) => p.vehicle.key).join("|");
  const fitToPins = useCallback(() => {
    const map = mapRef.current;
    if (!map || !pins.length || !window.google) return;
    if (pins.length === 1) {
      map.setCenter(pins[0].position);
      map.setZoom(Math.max(zoom, 13));
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    pins.forEach((p) => bounds.extend(p.position));
    map.fitBounds(bounds, 48);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinSignature, zoom]);

  useEffect(() => {
    fitToPins();
  }, [fitToPins]);

  const pinIcon = useCallback(
    (state, selected) => ({
      path: window.google.maps.SymbolPath.CIRCLE,
      fillColor: PIN_COLORS[state.key] || PIN_COLORS.stale,
      fillOpacity: 1,
      strokeColor: isDark ? "#0f172a" : "#ffffff",
      strokeWeight: 2,
      scale: selected ? 10 : 8,
    }),
    [isDark]
  );

  if (loadError) {
    return (
      <p className="app-muted px-4 py-10 text-center text-sm">
        Google Maps failed to load, so the map is unavailable. The list view still works.
      </p>
    );
  }

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-sm" style={{ height }}>
        <Spinner size={18} /> <span className="app-muted">Loading map…</span>
      </div>
    );
  }

  const selected = pins.find((p) => p.vehicle.key === selectedKey);

  return (
    <GoogleMap
      mapContainerStyle={{ width: "100%", height }}
      center={DEFAULT_CENTER}
      zoom={zoom}
      onLoad={(map) => {
        mapRef.current = map;
        fitToPins();
      }}
      onUnmount={() => {
        mapRef.current = null;
      }}
      onClick={() => setSelectedKey(null)}
      options={{
        styles: isDark ? MAP_STYLE_DARK : MAP_STYLE_LIGHT,
        mapTypeControl: false,
        streetViewControl: false,
        clickableIcons: false,
        gestureHandling: "greedy",
      }}
    >
      {pins.map((pin) => (
        <MarkerF
          key={pin.vehicle.key}
          position={pin.position}
          title={`${pin.vehicle.label} — ${pin.state.label}`}
          icon={pinIcon(pin.state, pin.vehicle.key === selectedKey)}
          zIndex={pin.vehicle.key === selectedKey ? 1000 : pin.state.key === "driving" ? 500 : 1}
          onClick={() => setSelectedKey(pin.vehicle.key)}
        />
      ))}

      {selected && (
        <InfoWindowF position={selected.position} onCloseClick={() => setSelectedKey(null)}>
          {/* Google's info window is always white — keep its contents light. */}
          <div className="app-light-surface max-w-[15rem] text-slate-800">
            <p className="text-base font-bold">{selected.vehicle.label}</p>
            {vehicleModel(selected.vehicle.info) && (
              <p className="text-xs text-slate-500">{vehicleModel(selected.vehicle.info)}</p>
            )}
            <p className="mt-1 text-sm font-semibold">{selected.state.label}</p>
            {gpsPlace(selected.vehicle.gps) && <p className="text-xs">{gpsPlace(selected.vehicle.gps)}</p>}
            {selected.vehicle.gps.driver && (
              <p className="text-xs text-slate-500">Driver: {selected.vehicle.gps.driver}</p>
            )}
            {selected.vehicle.gps.ageMinutes !== null && (
              <p className="text-xs text-slate-500">Reported {formatAge(selected.vehicle.gps.ageMinutes)} ago</p>
            )}
            <div className="mt-2 flex gap-3 text-xs font-semibold">
              {showLinks && (
                <Link to={`/fleet/vehicles/${selected.vehicle.key}`} className="text-sky-700 hover:underline">
                  Open vehicle
                </Link>
              )}
              <a
                href={mapsUrl(selected.position.lat, selected.position.lng)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sky-700 hover:underline"
              >
                Google Maps
              </a>
            </div>
          </div>
        </InfoWindowF>
      )}
    </GoogleMap>
  );
};

export const MapLegend = ({ counts }) => (
  <div className="app-muted flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
    {MAP_LEGEND.map((item) => (
      <span key={item.key} className="inline-flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: PIN_COLORS[item.key] }} />
        {item.label}
        {counts && <span className="tabular-nums">({counts[item.key] || 0})</span>}
      </span>
    ))}
  </div>
);

export default FleetMap;
